import {FC, useCallback, useEffect, useState} from 'react'
import {ID, isNotEmpty, numberWithCommas} from '../../../../../../_metronic/helpers'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'
import {ProductPrice} from '../core/_models'
import {deleteProductPrice, getProductPrices, saveProductPrice} from '../core/_requests'

type Props = {
  productServiceId?: ID
  categoryId?: ID
  title?: string
  onChanged?: () => void
  readOnly?: boolean
  readOnlyReason?: string
}

const ProductPriceHistoryTable: FC<Props> = ({
  productServiceId,
  categoryId,
  title = 'قیمت‌های دوره‌ای',
  onChanged,
  readOnly = false,
  readOnlyReason,
}) => {
  const [prices, setPrices] = useState<ProductPrice[]>([])
  const [selectedPrice, setSelectedPrice] = useState<ProductPrice | undefined>()
  const [fromDate, setFromDate] = useState('')
  const [price, setPrice] = useState('')
  const [discountPercent, setDiscountPercent] = useState('0')
  const [editorOpen, setEditorOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState<string | undefined>()

  const ownerIsReady = Boolean(productServiceId || categoryId)
  const editorTitle = selectedPrice?.id ? 'ویرایش قیمت دوره‌ای' : 'ثبت قیمت دوره‌ای'
  const discountPercentValue = Number(discountPercent)
  const discountPercentIsInvalid =
    !Number.isFinite(discountPercentValue) || discountPercentValue < 0 || discountPercentValue > 99

  const loadPrices = useCallback(async () => {
    if (!ownerIsReady) {
      return
    }

    setMessage(undefined)
    const data = await getProductPrices({productServiceId, categoryId})
    setPrices(data)
  }, [categoryId, ownerIsReady, productServiceId])

  useEffect(() => {
    loadPrices()
  }, [loadPrices])

  const resetForm = () => {
    setSelectedPrice(undefined)
    setFromDate('')
    setPrice('')
    setDiscountPercent('0')
  }

  const closeEditor = () => {
    resetForm()
    setEditorOpen(false)
  }

  const openNewPrice = () => {
    if (readOnly) {
      return
    }

    resetForm()
    setEditorOpen(true)
  }

  const editPrice = (item: ProductPrice) => {
    if (readOnly) {
      return
    }

    setSelectedPrice(item)
    setFromDate(item.fd || '')
    setPrice(item.pr?.toString() || '')
    setDiscountPercent((item.dis ?? 0).toString())
    setEditorOpen(true)
  }

  const submit = async () => {
    if (readOnly || !isNotEmpty(fromDate) || !isNotEmpty(price) || discountPercentIsInvalid) {
      return
    }

    try {
      setSubmitting(true)
      setMessage(undefined)
      await saveProductPrice({
        id: selectedPrice?.id,
        pid: productServiceId ? Number(productServiceId) : null,
        cid: categoryId ? Number(categoryId) : null,
        fd: fromDate,
        pr: price,
        dis: discountPercent || 0,
      })
      closeEditor()
      await loadPrices()
      onChanged?.()
    } catch (error: any) {
      setMessage(error?.response?.data?.Message || error?.message || 'خطا در ذخیره قیمت')
    } finally {
      setSubmitting(false)
    }
  }

  const removePrice = async (item: ProductPrice) => {
    if (readOnly || !item.id || !window.confirm('این رکورد قیمت حذف شود؟')) {
      return
    }

    try {
      setSubmitting(true)
      setMessage(undefined)
      await deleteProductPrice(item.id)
      if (selectedPrice?.id === item.id) {
        resetForm()
      }
      await loadPrices()
      onChanged?.()
    } catch (error: any) {
      setMessage(error?.response?.data?.Message || error?.message || 'خطا در حذف قیمت')
    } finally {
      setSubmitting(false)
    }
  }

  if (!ownerIsReady) {
    return (
      <div className='mp-price-empty mt-6'>
        <div className='d-flex align-items-center'>
          <i className='las la-info-circle fs-1 text-primary me-3' />
          <span className='fw-bold text-primary'>
            برای ثبت قیمت دوره‌ای، ابتدا کالا/خدمت را ذخیره کنید.
          </span>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='mp-price-history mt-8'>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4'>
          <div>
            <h4 className='fw-bolder mb-1'>{title}</h4>
            <span className='text-gray-500 fs-7'>
              برای هر بازه فقط تاریخ شروع، مبلغ واحد و درصد تخفیف را ثبت کنید؛ پایان بازه خودکار از
              قیمت بعدی محاسبه می‌شود.
            </span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            {!readOnly && (
              <button
                type='button'
                className='btn btn-sm btn-primary'
                disabled={submitting}
                onClick={openNewPrice}
              >
                <i className='las la-plus fs-2 me-1' />
                ثبت قیمت جدید
              </button>
            )}
            <button
              type='button'
              className='btn btn-sm btn-icon btn-light'
              disabled={submitting}
              onClick={loadPrices}
              title='بروزرسانی'
            >
              <i className='las la-sync fs-2' />
            </button>
          </div>
        </div>

        {message && <div className='alert alert-danger py-3'>{message}</div>}
        {readOnly && readOnlyReason && (
          <div className='alert alert-info py-3'>{readOnlyReason}</div>
        )}

        <div className='mp-catalog-scroll-hint mt-5'>
          <i className='las la-arrows-alt-h fs-2' aria-hidden='true'></i>
          برای مشاهده همه ستون‌ها، جدول را به طرفین بکشید.
        </div>
        <div className='table-responsive mp-price-table mt-2'>
          <table className='table align-middle table-row-dashed fs-6 gy-4'>
            <thead>
              <tr className='text-muted fw-bolder fs-7'>
                <th>شروع اعتبار</th>
                <th>پایان اعتبار</th>
                <th className='text-end'>مبلغ واحد (ریال)</th>
                <th className='text-end'>تخفیف ردیف</th>
                {!readOnly && <th className='text-end'>عملیات</th>}
              </tr>
            </thead>
            <tbody className='text-gray-700 fw-bold'>
              {prices.length === 0 && (
                <tr>
                  <td colSpan={readOnly ? 4 : 5} className='text-center text-gray-500 py-8'>
                    هنوز قیمت دوره‌ای ثبت نشده است
                  </td>
                </tr>
              )}
              {prices.map((item) => (
                <tr key={`${item.id}`}>
                  <td>{item.fd}</td>
                  <td>
                    {item.td ? (
                      item.td
                    ) : (
                      <span className='badge badge-light-success'>تا ثبت قیمت بعدی</span>
                    )}
                  </td>
                  <td className='text-end fw-bolder text-gray-900'>
                    {numberWithCommas(item.pr?.toString() || '')}
                  </td>
                  <td className='text-end'>{item.dis ?? 0}%</td>
                  {!readOnly && (
                    <td className='text-end'>
                      <button
                        type='button'
                        className='btn btn-sm btn-icon btn-light-warning me-2'
                        disabled={submitting}
                        onClick={() => editPrice(item)}
                        title='ویرایش'
                      >
                        <i className='las la-edit fs-2' />
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-icon btn-light-danger'
                        disabled={submitting}
                        onClick={() => removePrice(item)}
                        title='حذف'
                      >
                        <i className='las la-trash fs-2' />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editorOpen && !readOnly && (
        <>
          <div
            className='modal fade show d-block mp-product-modal mp-catalog-modal mp-price-editor-modal'
            role='dialog'
            tabIndex={-1}
            aria-modal='true'
          >
            <div className='modal-dialog modal-dialog-centered modal-md modal-fullscreen-sm-down'>
              <div className='modal-content'>
                <div className='modal-header'>
                  <div>
                    <h4 className='fw-bolder mb-1'>{editorTitle}</h4>
                    <span className='text-gray-500 fs-7'>
                      تاریخ پایان این بازه به صورت خودکار از قیمت بعدی محاسبه می‌شود.
                    </span>
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon btn-light'
                    disabled={submitting}
                    onClick={closeEditor}
                    title='بستن'
                  >
                    <i className='las la-times fs-2' />
                  </button>
                </div>
                <div className='modal-body'>
                  <div className='mb-5'>
                    <CustomInputs
                      type='datepicker'
                      label='شروع اعتبار'
                      isRequired
                      isDisabled={submitting}
                      stateValue={{value: fromDate, setValue: setFromDate}}
                    />
                  </div>
                  <div className='mb-5'>
                    <CustomInputs
                      type='currency'
                      label='مبلغ واحد (ریال)'
                      isRequired
                      isDisabled={submitting}
                      stateValue={{value: price, setValue: setPrice}}
                    />
                  </div>
                  <div>
                    <CustomInputs
                      type='number'
                      numberAsText
                      label='تخفیف ردیف (%)'
                      isDisabled={submitting}
                      stateValue={{value: discountPercent, setValue: setDiscountPercent}}
                      error={
                        discountPercentIsInvalid ? (
                          <span className='fs-7 text-danger'>
                            درصد تخفیف باید بین ۰ تا ۹۹ باشد.
                          </span>
                        ) : undefined
                      }
                    />
                  </div>
                </div>
                <div className='modal-footer'>
                  <button
                    type='button'
                    className='btn btn-light'
                    disabled={submitting}
                    onClick={closeEditor}
                  >
                    انصراف
                  </button>
                  <button
                    type='button'
                    className='btn btn-success'
                    disabled={
                      submitting ||
                      !isNotEmpty(fromDate) ||
                      !isNotEmpty(price) ||
                      discountPercentIsInvalid
                    }
                    onClick={submit}
                  >
                    <i className='las la-save fs-2 me-1' />
                    ذخیره
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='modal-backdrop fade show mp-price-editor-backdrop'></div>
        </>
      )}
    </>
  )
}

export {ProductPriceHistoryTable}
